import { Button, Dialog } from '@mui/material'
import React, { useEffect, useState } from 'react'
import useAuth from '../../../components/hooks/useAuth'
import { useTheme } from '../../../components/hooks/useTheme'
import { validateMin } from '../../../utils/validateMin';
import { post_Refund } from '../../../services/refund';
import { branchTimezone } from '../../../utils/summaryCalculation';
import useConfirm from '../../../components/hooks/useConfirm';
import colorConfigs from '../../../configs/colorConfigs';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';
import moment from 'moment';
import useGeneral from '../../../components/hooks/useGeneral';

type PaymentDetailsPopupProps = {
    open: boolean;
    onClose: () => void;
    paymentDetails: any;
    creditCardList: any[];
    setPleaseWait: (value: boolean) => void;
    formattedCreatedAt: string;
    isCompletedOrCancelled: boolean;
    isSecurityDepositedRefunded: boolean;
    reservationId: number;
    summaryData: any;
    refreshTheReservation: () => void;
    refundList: any[]
}

const PaymentDetailsPopup = ({
    open,
    onClose,
    paymentDetails,
    creditCardList,
    setPleaseWait,
    formattedCreatedAt,
    isCompletedOrCancelled,
    isSecurityDepositedRefunded,
    reservationId,
    summaryData,
    refreshTheReservation,
    refundList,
}: PaymentDetailsPopupProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { theme, themeStyles } = useTheme();
    const { dateFormat }: any = useGeneral();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [creditCardDetails, setCreditCardDetails] = useState<any | null>(null);
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [openRefundPopup, setOpenRefundPopup] = useState<boolean>(false);
    const [showTheRefundBtn, setShowTheRefundBtn] = useState<boolean>(true);
    const [isPaymentType, setIsPaymentType] = useState<boolean>(true);
    const UpdateTimezone = branchTimezone(formattedCreatedAt);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (open) {
            const selectedCreditCardDetails = creditCardList?.find((creditCard) => creditCard?.id === paymentDetails?.creditCardId);
            if (selectedCreditCardDetails) {
                setCreditCardDetails(selectedCreditCardDetails);
            } else {
                setCreditCardDetails(null);
            }

            if (paymentDetails?.paymentType === "Security Deposit" && paymentDetails?.status === "Approved") {
                setShowTheRefundBtn(!isSecurityDepositedRefunded);
                let totalRefundSecurityAmount = refundList?.reduce((sum, refund) => {
                    return (refund?.refundType === "Security Deposit" && refund?.refundStatus === "Approved") ? sum + refund?.amount : sum;
                }, 0) || 0;

                const amountToRefund = (totalRefundSecurityAmount >= summaryData?.securityDepositAmountPaid)
                    ? 0.0
                    : (summaryData?.securityDepositAmountPaid - totalRefundSecurityAmount);

                setRefundAmount(amountToRefund || 0.00);
                setIsPaymentType(false);
            } else {
                if (summaryData?.outstandingBalance < 0) {
                    setRefundAmount(Math.abs(summaryData?.outstandingBalance) || 0.00);
                } else {
                    setRefundAmount(0.0);
                }

                setShowTheRefundBtn(paymentDetails?.status === "Approved");
                setIsPaymentType(true);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, creditCardList, paymentDetails])

    const onRefundBtnClick = () => {
        if ((!isCompletedOrCancelled || summaryData?.status === "Cancelled" || summaryData?.status === "Cancelled Paid") && showTheRefundBtn) {
            setOpenRefundPopup(true);
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWait(true);

        // Prevent submission if refund amount is more than allowed
        if (refundAmount > paymentDetails?.amount) {
            setConfirmData({
                title: 'Warning',
                summary: "Refund amount cannot exceed the original payment amount.",
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
            setPleaseWait(false);
            return;
        }

        if (refundAmount > 0) {
            let refundType = "Refund";
            if (paymentDetails?.paymentType === "Security Deposit") {
                refundType = "Security Deposit";
            }

            await post_Refund({
                reservationId: reservationId,
                refundType: refundType || "Refund",
                dateofRefund: new Date(UpdateTimezone).toLocaleString(),
                amount: refundAmount,
                reference: paymentDetails?.reference,
                createdAt: new Date(UpdateTimezone).toLocaleString(),
                createdBy: auth.givenname,
                syncedWithAccounting: false,
                refundMethod: paymentDetails?.paymentMethod || "Credit Card",
                refundStatus: "Approved"
            }, auth?.id, tenantId)
                .then(() => {
                    refreshTheReservation();
                    onClose();
                    handleTenantError(null);
                })
                .catch((error) => {
                    if (
                        error?.response?.status === 400 &&
                        error?.response?.data === "Refund cannot be processed until the payment is settled. Please try again after 24 hours."
                    ) {
                        setConfirmData({
                            title: 'Warning',
                            summary: error.response.data,
                            confirmBtn: 'Ok',
                            isConfirm: (callback: any) => {
                                callback();
                            }
                        });

                        setConfirmPopup(true);
                    } else {
                        handleTenantError(error);
                    }

                    if (error?.response?.data?.includes("Declined")) {
                        refreshTheReservation();

                        setConfirmData({
                            title: 'Warning',
                            summary: error?.response?.data,
                            confirmBtn: 'Ok',
                            isConfirm: (callback: any) => {
                                callback();
                            }
                        });

                        setConfirmPopup(true);
                    }

                    setPleaseWait(false);
                    return;
                });
        }

        setOpenRefundPopup(false);
    }


    return (
        <>
            <Dialog
                open={open}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => {
                            setCreditCardDetails(null);
                            onClose();
                        }}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit Payment</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div>
                                            <div className="flex flex-col gap-y-[0.5rem]">
                                                <div>
                                                    <div className={`flex justify-between items-center border-b mb-[0.75rem] ${themeStyles.main.borderColor}`}>
                                                        <div className="flex items-center gap-1.5">
                                                            <h2 className={`text-sm font-semibold ${themeStyles.main.textHeadingColor}`}>Payment Information</h2>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                        <div className="space-y-4">
                                                            <div className="flex flex-col gap-y-[0.125rem]">
                                                                <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Payment Type</label>
                                                                <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{paymentDetails?.paymentType || "--"}</span>
                                                            </div>
                                                        </div>

                                                        <div />

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Payment Method</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{paymentDetails?.paymentMethod || "--"}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>${parseFloat(paymentDetails?.amount)?.toFixed(2) || 0.00}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Payment Status</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{paymentDetails?.status || "--"}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Date</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>
                                                                {
                                                                    paymentDetails?.dateofPayment
                                                                        ? moment(
                                                                            new Date(
                                                                                paymentDetails?.dateofPayment
                                                                                    ?.replaceAll("-", "/")
                                                                                    ?.split("T")[0]
                                                                            )
                                                                        ).format(dateFormat)
                                                                        : "--"
                                                                }
                                                            </span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Reference</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{paymentDetails?.reference || "--"}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {
                                                    paymentDetails?.paymentMethod === "Credit Card" && (
                                                        <div>
                                                            <div className={`flex justify-between items-center border-b mt-[1rem] mb-[0.75rem] ${themeStyles.main.borderColor}`}>
                                                                <div className="flex items-center gap-1.5">
                                                                    <h2 className={`text-sm font-semibold ${themeStyles.main.textHeadingColor}`}>Payment Method Details</h2>
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                                <div className="flex flex-col gap-y-[0.125rem]">
                                                                    <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Cardholder's Name</label>
                                                                    <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{creditCardDetails?.cardholderName || "--"}</span>
                                                                </div>

                                                                <div className="flex flex-col gap-y-[0.125rem]">
                                                                    <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Brand</label>
                                                                    <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{creditCardDetails?.brand || "--"}</span>
                                                                </div>

                                                                <div className="flex flex-col gap-y-[0.125rem]">
                                                                    <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Last 4 Digits</label>
                                                                    <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{creditCardDetails?.creditCardNumber || "--"}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>

                                            <div className="flex items-center gap-4 mt-6">
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => {
                                                        setCreditCardDetails(null);
                                                        onClose();
                                                    }}
                                                >
                                                    Cancel
                                                </Button>

                                                {
                                                    showTheRefundBtn && (
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            sx={{
                                                                backgroundColor: colorConfigs.btn.delete.bg
                                                            }}
                                                            onClick={onRefundBtnClick}
                                                        >
                                                            Refund
                                                        </Button>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            {
                showTheRefundBtn && (
                    <Dialog
                        open={openRefundPopup}
                        maxWidth="md"
                        fullScreen={false}
                        sx={{
                            '& .MuiDialog-paper': {
                                maxWidth: '600px',
                                width: '100%',
                                borderRadius: '16px',
                            },
                        }}
                    >
                        <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320.591 320.591"
                                className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                                onClick={() => setOpenRefundPopup(false)}
                            >
                                <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                                <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                            </svg>
                            <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                                <div className="w-full mt-2">
                                    <div className={`w-full`}>
                                        <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                            <div className="flex items-center gap-1.5">
                                                <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Refund Transaction</h2>
                                            </div>
                                        </div>

                                        <div className="mt-2 p-2">
                                            <div className="w-full">
                                                <form onSubmit={handleSubmit}>
                                                    <p className="text-[10px] mt-2 bg-[#fdf8e3] text-[#8a6d3b] text-left rounded-sm py-2 pl-4 pr-6">
                                                        Once you click the "Refund" button the action can't be reversed, please review the amount before clicking on refund transaction
                                                    </p>

                                                    <div className='w-full mt-2'>
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Amount to refund</span>
                                                        <br />
                                                        {
                                                            isPaymentType
                                                                ? (
                                                                    <input
                                                                        type="number"
                                                                        step={0.01}
                                                                        min={0}
                                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                        value={refundAmount}
                                                                        onChange={(e) => setRefundAmount(validateMin(e.target.value, 0))}
                                                                        required
                                                                    />
                                                                ) : (
                                                                    <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{refundAmount || 0}</span>
                                                                )
                                                        }
                                                    </div>

                                                    <div className="flex gap-4 mt-6">
                                                        <Button
                                                            variant="contained"
                                                            type="button"
                                                            sx={{
                                                                backgroundColor: colorConfigs.btn.cancel.bg,
                                                                border: colorConfigs.btn.cancel.border,
                                                                color: colorConfigs.btn.cancel.color
                                                            }}
                                                            onClick={() => setOpenRefundPopup(false)}
                                                        >
                                                            Cancel
                                                        </Button>

                                                        {
                                                            (refundAmount > 0 && paymentDetails?.status === "Approved") && (
                                                                <Button variant="contained"
                                                                    sx={{
                                                                        backgroundColor: colorConfigs.btn.delete.bg
                                                                    }}
                                                                    type="submit"
                                                                >
                                                                    Refund
                                                                </Button>
                                                            )
                                                        }
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                )
            }
        </>
    )
}

export default PaymentDetailsPopup